import { useCallback, useEffect, useState } from 'react';

import type { SelectModalFieldWithCustom as SelectModalFieldWithCustomType } from '../types';

interface UseCustomDeadlineValuesProps {
  value: unknown;
  field: SelectModalFieldWithCustomType;
  onValueChange: (value: unknown) => void;
  onModalClose: () => void;
}

export const useCustomDeadlineValues = ({
  value,
  field,
  onValueChange,
  onModalClose,
}: UseCustomDeadlineValuesProps) => {
  const [customDayValue, setCustomDayValue] = useState<number | null>(null);
  const [customHourValue, setCustomHourValue] = useState<number | null>(null);

  useEffect(() => {
    if (typeof value === 'string' && value.startsWith('custom_')) {
      const [, dayLabel, timeLabel] = value.split('_');
      const { customDeadlineOptions } = field;

      if (customDeadlineOptions) {
        const dayOption = customDeadlineOptions.dayOfWeekOptions?.find((d) => d.label === dayLabel);
        const hourOption = customDeadlineOptions.hourOptions?.find((h) => h.label === timeLabel);

        if (dayOption) setCustomDayValue(dayOption.value);
        if (hourOption) setCustomHourValue(hourOption.value);
      }
    }
  }, [value, field]);

  const handleCustomDayChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const dayValue = Number(e.target.value);
      setCustomDayValue(dayValue);

      if (customHourValue !== null) {
        const dayOption = field.customDeadlineOptions?.dayOfWeekOptions?.find(
          (d) => d.value === dayValue
        );
        const hourOption = field.customDeadlineOptions?.hourOptions?.find(
          (h) => h.value === customHourValue
        );

        if (dayOption && hourOption) {
          const customValue = `custom_${dayOption.label}_${hourOption.label}`;
          onValueChange(customValue);
          onModalClose();
        }
      }
    },
    [customHourValue, onValueChange, field.customDeadlineOptions, onModalClose]
  );

  const handleCustomHourChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const hourValue = Number(e.target.value);
      setCustomHourValue(hourValue);

      if (customDayValue !== null) {
        const dayOption = field.customDeadlineOptions?.dayOfWeekOptions?.find(
          (d) => d.value === customDayValue
        );
        const hourOption = field.customDeadlineOptions?.hourOptions?.find(
          (h) => h.value === hourValue
        );

        if (dayOption && hourOption) {
          const customValue = `custom_${dayOption.label}_${hourOption.label}`;
          onValueChange(customValue);
          onModalClose();
        }
      }
    },
    [customDayValue, onValueChange, field.customDeadlineOptions, onModalClose]
  );

  return {
    customDayValue,
    customHourValue,
    handleCustomDayChange,
    handleCustomHourChange,
  };
};
